// Routes (docs/08-app-design.md section 4). The public pages (home,
// Protection, not found; src/site) and /verify need no session; the
// onboarding screens need no session; every app screen needs an unlocked
// session and redirects to /locked or /welcome otherwise. A device whose
// database a later build has upgraded (session state 'newer-build', the
// version fence of docs/18-one-fee-reserve.md section 3, writer step 6)
// is sent to /welcome from every entrance, where Welcome says so and
// offers neither Create nor Restore.

import { type ReactNode, Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { Create } from './pages/Create';
import { Deposit } from './pages/Deposit';
import { Keys } from './pages/Keys';
import { Locked } from './pages/Locked';
import { Restore } from './pages/Restore';
import { Settings } from './pages/Settings';
import { Stake } from './pages/Stake';
import { Swap } from './pages/Swap';
import { Vault } from './pages/Vault';
import { Verify } from './pages/Verify';
import { Welcome } from './pages/Welcome';
import { Withdraw } from './pages/Withdraw';
import { NotFound } from './site/NotFound';
import { Protection } from './site/Protection';
import { useSession } from './session';

/** The landing page (src/site/Home.tsx): loaded only on "/", so its page-wide stylesheet never reaches the app's screens. */
const Home = lazy(() => import('./site/Home'));

function RequireUnlocked({ children }: { children: ReactNode }) {
  const { state } = useSession();
  if (state === 'starting') return null;
  if (state === 'unlocked') return <>{children}</>;
  // 'no-vault' and 'newer-build' both go to Welcome, which tells them apart.
  return <Redirect to={state === 'locked' ? '/locked' : '/welcome'} replace />;
}

/**
 * /locked under the version fence (session state 'newer-build'): this build cannot read the device's setup, so its
 * door would only fail; Welcome shows the newer-build sentence instead.
 */
function RefuseNewerBuild({ children }: { children: ReactNode }) {
  const { state } = useSession();
  if (state === 'newer-build') return <Redirect to="/welcome" replace />;
  return <>{children}</>;
}

/**
 * /create and /restore refuse an occupied device (R48): entering either route while locked or unlocked is
 * redirected away rather than letting onboarding overwrite an existing setup. Once let in (this mount ever saw
 * 'no-vault'), it keeps rendering the flow regardless of later session changes: both Create and Restore call
 * `session.adoptUnlocked()` partway through their own steps, which flips the session to 'unlocked' before the
 * screen is done (Restore's mandatory key refresh runs after that point) - re-evaluating the guard on every
 * session change would redirect the flow away from itself mid-completion. The storage boundary
 * (`installSetup`'s `DeviceOccupied`) is the guard that still holds regardless of this one.
 */
function RequireNoVault({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const allowed = useRef(false);
  if (state === 'no-vault') allowed.current = true;
  if (state === 'starting') return null;
  if (allowed.current) return <>{children}</>;
  return <Redirect to={state === 'unlocked' ? '/vault' : '/welcome'} replace />;
}

/**
 * A client-side move to another page starts at its top, and keyboard and
 * screen-reader focus moves to the new page's main landmark (unless the page
 * already focused a field of its own). Back and Forward keep the browser's own
 * scroll restoration; a #section link is left to the page that owns it.
 */
function RouteChange() {
  const [location] = useLocation();
  const first = useRef(true);
  const popped = useRef(false);
  useEffect(() => {
    const onPop = () => {
      popped.current = true;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (popped.current) {
      popped.current = false;
      return;
    }
    if (window.location.hash) return;
    window.scrollTo(0, 0);
    const frame = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (!active || active === document.body) document.getElementById('main')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [location]);
  return null;
}

export function App() {
  const { state } = useSession();
  // "Explore Vault": straight into an unlocked session, to the unlock screen for a
  // sealed setup on this device, otherwise to Welcome (create-or-restore, or
  // the newer-build sentence).
  const launchHref = state === 'locked' ? '/locked' : state === 'unlocked' ? '/vault' : '/welcome';
  // the landing page loads the first time "/" is shown, then stays mounted (hidden elsewhere): see src/site/Home.tsx
  const [location] = useLocation();
  const [landingShown, setLandingShown] = useState(location === '/');
  useEffect(() => {
    if (location === '/') setLandingShown(true);
  }, [location]);
  return (
    <>
      <RouteChange />
      <Switch>
        {/* "/" is the landing page, mounted below the routes once shown (src/site/Home.tsx) */}
        <Route path="/">{null}</Route>
        <Route path="/protection">
          <Protection launchHref={launchHref} />
        </Route>
        <Route path="/welcome">
          <Welcome />
        </Route>
        <Route path="/create">
          <RequireNoVault>
            <Create />
          </RequireNoVault>
        </Route>
        <Route path="/restore">
          <RequireNoVault>
            <Restore />
          </RequireNoVault>
        </Route>
        <Route path="/locked">
          <RefuseNewerBuild>
            <Locked />
          </RefuseNewerBuild>
        </Route>
        <Route path="/vault">
          <RequireUnlocked>
            <Vault />
          </RequireUnlocked>
        </Route>
        <Route path="/keys">
          <RequireUnlocked>
            <Keys />
          </RequireUnlocked>
        </Route>
        <Route path="/swap">
          <RequireUnlocked>
            <Swap />
          </RequireUnlocked>
        </Route>
        <Route path="/deposit">
          <RequireUnlocked>
            <Deposit />
          </RequireUnlocked>
        </Route>
        <Route path="/stake">
          <RequireUnlocked>
            <Stake />
          </RequireUnlocked>
        </Route>
        <Route path="/withdraw">
          <RequireUnlocked>
            <Withdraw />
          </RequireUnlocked>
        </Route>
        <Route path="/settings">
          <RequireUnlocked>
            <Settings />
          </RequireUnlocked>
        </Route>
        <Route path="/verify">
          {/* Needs no unlock (docs/08 5.10): anyone may check the deployment. */}
          <Verify />
        </Route>
        <Route>
          <NotFound launchHref={launchHref} />
        </Route>
      </Switch>
      {/* after the routes: on any other route the hidden landing page's ids (the card art's) come second, so a
          page's own references (url(#…), #main) resolve to its own elements */}
      {landingShown ? (
        <Suspense fallback={null}>
          <Home launchHref={launchHref} visible={location === '/'} />
        </Suspense>
      ) : null}
    </>
  );
}
